import React from 'react';
import { useSelector } from 'react-redux';

import { Card, Statistic, Row, Col, Typography } from 'antd';
import { AreaChart } from 'components';

import {
  HISTORICAL_SCENARIO,
  LIMIT_METRIC_CHOICES,
  REAL_TIME_SCENARIO,
  selectors,
} from 'state';
import {
  formatters,
  isPredeterminedSession,
  predeterminedDayNumber,
} from 'services';
import { useReportingCurrency } from '../../state/app/utils';
import ThresholdChart from './ApexChartsMultiZone';
// import ThresholdChart from './HighchartsMultiZone';
import {
  metricsNameDict,
  useMetricsData,
  useMetricsKeys,
} from './useMetricsData';
import MetricsLimits2 from '../TrainerSession/SessionSettings/MetricsLimits2';

const Metrics: React.FC = () => {
  const metrics = useSelector(selectors.metrics);
  const portfolioDurationShort = useSelector(selectors.portfolioDurationShort);
  const portfolioDurationLong = useSelector(selectors.portfolioDurationLong);
  // const portfolioDv01 = useSelector(selectors.portfolioDv01);
  const hasHTM = useSelector(selectors.sessionHasHTMBonds);
  const hasAFS = useSelector(selectors.sessionHasAFSBonds);
  const HTMorAFS = hasAFS || hasHTM;

  const {
    // market_value,
    last_market_value,
    // portfolio_return,
    last_portfolio_return,
    session,
  } = useSelector(selectors.player);
  const { scenario_type, display_metrics, metrics_limits } = useSelector(
    selectors.activeSession
  );
  // console.log(metrics_limits);
  const noMetricsEnforced = metrics_limits.every(
    item => item.enforced === false
  );
  // console.log(`noMetricsEnforced: ${noMetricsEnforced}`);

  const sessionObj = useSelector(selectors.activeSession);
  const isRealTime = sessionObj.scenario_type === REAL_TIME_SCENARIO;

  const lastMetrics = { ...(metrics.slice(-1)[0] || {}) };
  delete lastMetrics.timestamp;
  delete lastMetrics.id;

  const reportingCurrency = useReportingCurrency(session);
  const data = useMetricsData();
  // TODO: add VaR back
  const finalData = data;
  // .filter(
  //   item =>
  //     !(
  //       ['Duration (long/short)', 'Portfolio DV01'].includes(item.name) &&
  //       scenario_type === HISTORICAL_SCENARIO
  //     )
  // )
  // .filter((_, index) => {
  //   // console.log(index);
  //   // console.log(_);
  //   // return display_metrics.some(i => i === index);
  //   return display_metrics.includes(_.key);
  //   // return display_metrics.some(i => i === _.key);
  // });

  const metricsKeys = useMetricsKeys();
  const title =
    sessionObj.limit_metric !== null
      ? `${LIMIT_METRIC_CHOICES[sessionObj.limit_metric]} Metric Limits`
      : '';

  const xaxis = isRealTime
    ? undefined
    : {
        tooltip: {
          enabled: false,
        },
        labels: {
          formatter: function (value: unknown) {
            return `Day ${value}`;
          },
          show: false,
        },
      };

  // console.log('finalData', finalData);
  const colSpan = noMetricsEnforced ? 24 : 18;
  return (
    <div>
      <Row gutter={80}>
        <Col
          span={colSpan}
          style={{
            // minWidth: 330,
            display: 'flex',
            flexDirection: 'column',
            // gap: 30,
          }}
        >
          <Row>
            {finalData.map(item => {
              const selectedMetricLimits = metrics_limits.find(
                metric => metric.metric_type === item.key
              );
              let style = { color: 'black' };
              if (
                selectedMetricLimits !== undefined &&
                selectedMetricLimits.enforced
              ) {
                const hasOnlyUpperLimit =
                  selectedMetricLimits.upper_limit !== null &&
                  selectedMetricLimits.lower_limit === null;
                const hasOnlyLowerLimit =
                  selectedMetricLimits.upper_limit === null &&
                  selectedMetricLimits.lower_limit !== null;
                if (hasOnlyUpperLimit) {
                  if (item.raw_value > selectedMetricLimits.upper_limit) {
                    style = { color: 'red' };
                  }
                } else if (hasOnlyLowerLimit) {
                  if (item.raw_value < selectedMetricLimits.lower_limit) {
                    style = { color: 'red' };
                  }
                } else {
                  // hasBoth
                  if (
                    item.raw_value < selectedMetricLimits.lower_limit ||
                    item.raw_value > selectedMetricLimits.upper_limit
                  ) {
                    style = { color: 'red' };
                  }
                }
              }
              return (
                <Col key={item.name}>
                  <Card>
                    <Statistic
                      title={item.name}
                      value={item.value}
                      valueStyle={style}
                      style={{ height: '58px' }}
                    />
                  </Card>
                </Col>
              );
            })}
          </Row>
          <br />
          <Row>
            {metricsKeys.map(key => {
              console.log(`metricsKeys: ${key}`);
              if (!metricsNameDict[key]) return null;

              const metrics_limits_index = metricsNameDict[key].limit_index;
              if (!metrics_limits[metrics_limits_index].visible) return null;

              const data = metrics.map(
                (item: { [key: string]: any }, index) => ({
                  // x: item.timestamp,
                  // Predetermined: the real "Day N" from the row's simulated date --
                  // index * frequency is meaningless there (frequency is vestigial).
                  x:
                    isRealTime || sessionObj.show_graph_as_rt
                      ? item.timestamp
                      : isPredeterminedSession(sessionObj)
                        ? predeterminedDayNumber(
                            item.scenario_date ?? item.timestamp,
                            sessionObj.start_date,
                          )
                        : index * sessionObj.frequency + 1,
                  y: item[key] as any,
                })
              );

              return (
                <Col lg={8} key={key}>
                  {/*CHANGED FROM HIGHCHARTS TO APEXCHARTS*/}
                  <ThresholdChart
                    data={data}
                    height={270}
                    width={400}
                    metricInfoDict={metricsNameDict[key]}
                    show_linear_regression={true}
                  />
                </Col>
              );
            })}
          </Row>
        </Col>
        {!noMetricsEnforced && (
          <Col
            span={6}
            style={{
              // minWidth: 400,
              borderLeft: '2px solid #eeeeee',
              display: 'flex',
              flexDirection: 'column',
              // gap: 30,
            }}
          >
            <MetricsLimits2 allowEditing={false} />
          </Col>
        )}
      </Row>
    </div>
  );
};

export default Metrics;
